import { type ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { SidebarInset, SidebarProvider } from "@volca/ui/components/ui/sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { TooltipProvider } from "@volca/ui/components/ui/tooltip";
import { useGetBusiness } from "@/business/query";
import { CHANGELOG_PATH } from "@/changelog/routes";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useImpersonation } from "@/core/impersonation";
import { useImpersonationBusinessOptions } from "@/core/impersonation/impersonation-options";
import { canShowImpersonationControl } from "@/nav/impersonation-access";
import { ImpersonationModal } from "@/nav/impersonation-modal";
import {
  canManageSimpleOnboarding,
  isSimpleOnboardingRequired,
} from "@/onboarding/routes";
import { useGetMyUser } from "@/user/query";
import { isAdmin, isApOnly, isTech } from "@/user/utils";
import { AppAsideProvider, AppAsideSlot } from "./app-aside";
import { AppSidebar } from "./app-sidebar";
import { BreadcrumbsProvider } from "./breadcrumbs-context";
import { CommandMenuProvider } from "./command-menu";
import { DevBar, useShowDevBar } from "./dev-bar";
import { ImpersonationFrame } from "./impersonation-frame";
import { NavColumnProvider } from "./nav-column-state";
import { isPathUnder, NAV_ITEM_DEFS } from "./nav-items";
import { PageArea } from "./page-area";
import { PublicHeader } from "./public-header";
import {
  ShellColumnSlot,
  ShellFrameProvider,
  ShellSidebar,
} from "./shell-frame";
import { NavColumn } from "./sidebar-nav-panel";
import { SkipLink } from "./skip-link";
import { useNavItems } from "./use-nav-items";
import { useSettingsNav } from "./use-settings-nav";
import { WorkspaceLayout } from "./workspace-layout";

const AppShellFrame = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { isAuthenticated, user, signedInUser } = useAuth();
  const { isImpersonating } = useImpersonation();
  const { data: userInfo, isLoading: isUserLoading } = useGetMyUser();
  const { businessId, isLoading: isBusinessesLoading } = useBusiness();
  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);
  const nav = useNavItems();
  const settings = useSettingsNav(nav.payrollSettings);
  const [isImpersonationOpen, setIsImpersonationOpen] = useState(false);
  const showDevBar = useShowDevBar();
  useImpersonationBusinessOptions(
    isAuthenticated && (isAdmin(user?.role) || isImpersonating),
  );

  const role = userInfo?.role ?? user?.role;
  const isOnboardingIncomplete =
    !isBusinessLoading && isSimpleOnboardingRequired(business?.onboarding_step);
  const isLimitedAccess =
    !isAuthenticated || (!isApOnly(role) && isOnboardingIncomplete);
  // The route guard can show setup recovery before it changes the URL, for
  // example when a business has no selected products or setup cannot load.
  const showOnboardingLayout =
    isPathUnder(pathname, "/onboarding") ||
    (isOnboardingIncomplete &&
      !isApOnly(role) &&
      canManageSimpleOnboarding(role));
  const showWorkspaceLayout = showOnboardingLayout || isTech(role);
  const showNav = nav.isReady && !isLimitedAccess && !showWorkspaceLayout;
  const items = showNav ? nav.items : [];
  const showSettings =
    showNav &&
    settings.items.length > 0 &&
    (nav.showSettings || settings.isActive);
  const activeItem = items.find((item) => item.isActive);
  const activeSection = activeItem?.sections.find(
    (section) => section.isActive,
  );
  const pageName =
    (activeItem &&
      (activeSection
        ? `${activeSection.label} · ${activeItem.label}`
        : activeItem.label)) ??
    settings.items.find((item) => item.isActive)?.title ??
    NAV_ITEM_DEFS.find((item) => isPathUnder(pathname, item.href))?.label ??
    (pathname === CHANGELOG_PATH ? "What’s new" : undefined) ??
    (pathname.startsWith("/profile") ? "Profile" : undefined);
  const titleFallback = showOnboardingLayout
    ? "Setup"
    : showWorkspaceLayout
      ? (pageName ?? "My pay")
      : pageName;

  if (!isAuthenticated) {
    return (
      <BreadcrumbsProvider>
        <ImpersonationFrame>
          <PublicHeader />
          <main className="min-h-0 w-full flex-1 [scrollbar-gutter:stable] overflow-y-auto [container:page/size]">
            {children}
          </main>
          {showDevBar && <DevBar />}
        </ImpersonationFrame>
      </BreadcrumbsProvider>
    );
  }

  const canImpersonate = !!canShowImpersonationControl({
    role: signedInUser?.role,
    email: signedInUser?.email,
  });
  const onImpersonate = canImpersonate
    ? () => setIsImpersonationOpen(true)
    : undefined;

  return (
    <TooltipProvider>
      <BreadcrumbsProvider fallback={titleFallback}>
        <AppAsideProvider>
          <ImpersonationFrame>
            <SkipLink />
            <SidebarProvider
              defaultOpen
              className="min-h-0 flex-1 overflow-hidden"
            >
              <CommandMenuProvider
                items={items}
                settings={showSettings ? settings : null}
                showHelp={!isLimitedAccess && !isTech(role)}
                onImpersonate={onImpersonate}
              >
                {showWorkspaceLayout ? (
                  <WorkspaceLayout onImpersonate={onImpersonate}>
                    {children}
                  </WorkspaceLayout>
                ) : (
                  <NavColumnProvider
                    items={items}
                    settings={showSettings ? settings : null}
                    isLimitedAccess={isLimitedAccess}
                    isLoading={
                      isUserLoading || isBusinessesLoading || isBusinessLoading
                    }
                  >
                    <ShellFrameProvider>
                      <Stack direction="row" className="min-h-0 w-full flex-1">
                        <AppSidebar
                          settings={showSettings ? settings : null}
                          onImpersonate={onImpersonate}
                        />
                        <ShellSidebar>
                          <NavColumn />
                          <ShellColumnSlot />
                        </ShellSidebar>
                        <SidebarInset className="min-w-0">
                          <PageArea>{children}</PageArea>
                        </SidebarInset>
                      </Stack>
                    </ShellFrameProvider>
                  </NavColumnProvider>
                )}
                <AppAsideSlot />
              </CommandMenuProvider>
            </SidebarProvider>
            {showDevBar && <DevBar onImpersonate={onImpersonate} />}
          </ImpersonationFrame>
          {isImpersonationOpen && (
            <ImpersonationModal
              open={isImpersonationOpen}
              onOpenChange={setIsImpersonationOpen}
            />
          )}
        </AppAsideProvider>
      </BreadcrumbsProvider>
    </TooltipProvider>
  );
};

export const AppShell = ({ children }: { children: ReactNode }) => (
  <AppShellFrame>{children}</AppShellFrame>
);
